// frontend/src/components/admin/EnedisCertExpiryBanner.jsx
// Bannière pleine largeur alertant les super-admins (rang 0) que le cert
// mTLS Enedis (env actif) approche de l'expiration ou est expiré.
// Silencieuse sinon ou en cas d'erreur backend.
//
// Aesthetic : warning-tape industriel (hachures diagonales subtiles) + date
// en monospace pour un feel SSL/audit. Slide-down à l'apparition,
// session-dismissible (X button, réapparaît au reload).

import { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { fetchWithAuth } from '../../services/api';

const HATCH_PATTERN = {
  backgroundImage: `repeating-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.08) 0,
    rgba(255, 255, 255, 0.08) 1px,
    transparent 1px,
    transparent 14px
  )`,
};

export default function EnedisCertExpiryBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchWithAuth('/api/admin/enedis/cert-status')
      .then((data) => { if (!cancelled) setStatus(data); })
      .catch(() => { if (!cancelled) setStatus(null); });
    return () => { cancelled = true; };
  }, []);

  if (!status?.success) return null;
  if (!status.isExpiringSoon && !status.isExpired) return null;
  if (dismissed) return null;

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });

  const envLabel = (status.env || '').toUpperCase();
  const daysAbs = Math.abs(status.daysUntilExpiry);

  return (
    <div
      role="alert"
      aria-live={status.isExpired ? 'assertive' : 'polite'}
      className="relative z-50 flex items-center gap-3 border-b border-red-800/40 bg-red-600 px-4 py-2.5 text-white shadow-lg animate-slideDown"
      style={HATCH_PATTERN}
    >
      <AlertTriangle
        className={`h-5 w-5 flex-shrink-0 ${status.isExpired ? 'animate-pulse' : ''}`}
        aria-hidden="true"
      />

      <span className="rounded bg-red-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
        {envLabel}
      </span>

      <span className="flex-1 text-sm font-medium leading-snug">
        {status.isExpired ? (
          <>
            Certificat Enedis <span className="font-semibold">EXPIRÉ</span> depuis{' '}
            <span className="rounded bg-red-800/50 px-1.5 py-0.5 font-mono font-semibold tracking-tight">
              {daysAbs} j
            </span>
            {' '}— les webservices SGE ne fonctionnent plus. Renouveler immédiatement.
          </>
        ) : (
          <>
            Certificat Enedis expire le{' '}
            <span className="rounded bg-red-800/50 px-1.5 py-0.5 font-mono font-semibold tracking-tight">
              {formatDate(status.notAfter)}
            </span>
            {' '}(dans{' '}
            <span className="font-mono font-semibold tracking-tight">
              {status.daysUntilExpiry} j
            </span>
            ) — prévoir le renouvellement.
          </>
        )}
      </span>

      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Masquer l'alerte pour cette session"
        className="flex-shrink-0 rounded p-1 transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-white/50"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
